'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertTriangle, FeatherFileText, FeatherInfo, FeatherRotateCcw, FeatherUserCheck } from '@subframe/core';
import type { Apuracao } from '../types';
import { formatarDataCurta } from '../utils/apuracaoFormatters';
interface ApuracaoBannersAlertasProps {
  apuracao: Apuracao;
}
const TRIAL_LABEL: Record<string, string> = {
  FREE: 'liberado sem cobrança',
  CHARGE: 'fatura demonstrativa R$ 0,00',
  BLOCK: 'bloqueado durante o trial'
};
export function ApuracaoBannersAlertas(t0) {
  const $ = _c(28);
  const {
    apuracao
  } = t0;
  const t1 = apuracao.eventosRejeitadosCount > 0;
  const t2 = apuracao.trialContext !== null;
  const t3 = apuracao.status === "REABERTO";
  let t4;
  if ($[0] !== apuracao.faturaEmitida || $[1] !== apuracao.pacoteAlerta || $[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = {
      erros: t1,
      pacote: apuracao.pacoteAlerta,
      trial: t2,
      reaberto: t3,
      fatura: apuracao.faturaEmitida
    };
    $[0] = apuracao.faturaEmitida;
    $[1] = apuracao.pacoteAlerta;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const tem = t4;
  if (!tem.erros && !tem.pacote && !tem.trial && !tem.reaberto && !tem.fatura) {
    return null;
  }
  let t5;
  if ($[6] !== apuracao.eventosRejeitadosCount || $[7] !== tem.erros) {
    t5 = tem.erros && <BannerLine tone="error" icon={<FeatherAlertTriangle />} titulo={`${apuracao.eventosRejeitadosCount} ${apuracao.eventosRejeitadosCount === 1 ? "evento precisa" : "eventos precisam"} de atenção antes do fechamento`} descricao="Veja a aba 'Eventos rejeitados' para corrigir a causa raiz e reprocessar." />;
    $[6] = apuracao.eventosRejeitadosCount;
    $[7] = tem.erros;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== apuracao.saldoPacote || $[10] !== tem.pacote) {
    t6 = tem.pacote && apuracao.saldoPacote && <BannerLine tone="warning" icon={<FeatherAlertTriangle />} titulo={`Saldo do pacote em ${apuracao.saldoPacote.percentualConsumido}%`} descricao={`${apuracao.saldoPacote.consumido} de ${apuracao.saldoPacote.total} unidades consumidas. Próximo reset em ${formatarDataCurta(apuracao.saldoPacote.proximoReset)}.`} />;
    $[9] = apuracao.saldoPacote;
    $[10] = tem.pacote;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== apuracao.trialContext || $[13] !== tem.trial) {
    t7 = tem.trial && apuracao.trialContext && <BannerLine tone="brand" icon={<FeatherUserCheck />} titulo={`Este ciclo está em período de trial — comportamento: ${TRIAL_LABEL[apuracao.trialContext.comportamento] ?? apuracao.trialContext.comportamento}`} descricao={`Trial expira em ${formatarDataCurta(apuracao.trialContext.dataExpiracao)}.`} />;
    $[12] = apuracao.trialContext;
    $[13] = tem.trial;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== apuracao.justificativaReabertura || $[16] !== apuracao.reabertoEm || $[17] !== apuracao.reabertoPor || $[18] !== tem.reaberto) {
    t8 = tem.reaberto && apuracao.reabertoEm && <BannerLine tone="warning" icon={<FeatherRotateCcw />} titulo={`Ciclo reaberto em ${formatarDataCurta(apuracao.reabertoEm)}${apuracao.reabertoPor ? ` por ${apuracao.reabertoPor}` : ""}`} descricao={apuracao.justificativaReabertura ?? "Sem justificativa registrada."} />;
    $[15] = apuracao.justificativaReabertura;
    $[16] = apuracao.reabertoEm;
    $[17] = apuracao.reabertoPor;
    $[18] = tem.reaberto;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== tem.fatura) {
    t9 = tem.fatura && <BannerLine tone="info" icon={<FeatherFileText />} titulo={"J\xE1 existe fatura ativa para este ciclo"} descricao={"N\xE3o \xE9 poss\xEDvel gerar outra fatura sem cancelar a atual. Reabrir o ciclo tamb\xE9m exige reemiss\xE3o."} />;
    $[20] = tem.fatura;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className="flex flex-col gap-3">{t5}{t6}{t7}{t8}{t9}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
interface BannerLineProps {
  tone: 'error' | 'warning' | 'brand' | 'info';
  icon: React.ReactNode;
  titulo: string;
  descricao: string;
}
function BannerLine(t0) {
  const $ = _c(21);
  const {
    tone,
    icon,
    titulo,
    descricao
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      error: "border-error-200 bg-error-50",
      warning: "border-warning-200 bg-warning-50",
      brand: "border-brand-200 bg-brand-50",
      info: "border-neutral-200 bg-neutral-50"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const wrapperByTone = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      error: "text-error-600",
      warning: "text-warning-600",
      brand: "text-brand-600",
      info: "text-neutral-500"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const iconByTone = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      error: "text-error-700",
      warning: "text-warning-800",
      brand: "text-brand-800",
      info: "text-neutral-800"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const titleByTone = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      error: "text-error-700",
      warning: "text-warning-700",
      brand: "text-brand-700",
      info: "text-neutral-600"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const descByTone = t4;
  const t5 = `flex items-start gap-3 rounded-[12px] border border-solid px-4 py-3 ${wrapperByTone[tone]}`;
  const t6 = `${iconByTone[tone]} mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center`;
  let t7;
  if ($[4] !== icon || $[5] !== t6) {
    t7 = <span className={t6}>{icon}</span>;
    $[4] = icon;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = `text-body-bold font-body-bold ${titleByTone[tone]}`;
  let t9;
  if ($[7] !== t8 || $[8] !== titulo) {
    t9 = <p className={t8}>{titulo}</p>;
    $[7] = t8;
    $[8] = titulo;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = `text-caption font-caption mt-0.5 ${descByTone[tone]}`;
  let t11;
  if ($[10] !== descricao || $[11] !== t10) {
    t11 = <p className={t10}>{descricao}</p>;
    $[10] = descricao;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t9) {
    t12 = <div className="flex-1">{t9}{t11}</div>;
    $[13] = t11;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FeatherInfo className="hidden h-4 w-4 text-transparent" />;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t12 || $[18] !== t5 || $[19] !== t7) {
    t14 = <div className={t5}>{t7}{t12}{t13}</div>;
    $[17] = t12;
    $[18] = t5;
    $[19] = t7;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  return t14;
}